import React from 'react';
import { Toaster } from 'sonner';
import { Skeleton } from './Skeleton';
import { Button } from './Button';
import { useTheme } from '../../lib/theme';

// Feedback surfaces — `BBM Context for claude/feedback.md` (Jaylen's
// Feedback Showroom picks, 2026-09-15): 1B dark pill toasts bottom-center
// with Undo · 3B skeleton rows · 4B short field errors + summary banner +
// error card · 5C autosave status line.
// (2B confirms live in ConfirmDialog.jsx / useConfirm.jsx.)

// ── Toasts (1B) ───────────────────────────────────────────────────────────
// One <AppToaster/> per tree. Dark pill in light mode, light pill in dark
// mode; bottom-center so it never covers header actions; a small type icon
// comes from Sonner. `useToast().show(msg, type, { undo })` adds an Undo
// action and stretches the duration.
export function AppToaster() {
    const { isDark } = useTheme();
    return (
        <Toaster
            position="bottom-center"
            theme={isDark ? 'light' : 'dark'}
            duration={3500}
            visibleToasts={3}
            offset={16}
            mobileOffset={20}
            // Brand ink instead of Sonner's pure black / white pills.
            style={isDark
                ? { '--normal-bg': '#e6e8ee', '--normal-text': '#0f172a', '--normal-border': '#e6e8ee', '--success-bg': '#e6e8ee', '--success-text': '#0f172a', '--error-bg': '#e6e8ee', '--error-text': '#0f172a', '--info-bg': '#e6e8ee', '--info-text': '#0f172a', '--warning-bg': '#e6e8ee', '--warning-text': '#0f172a' }
                : { '--normal-bg': '#0f172a', '--normal-text': '#ffffff', '--normal-border': '#0f172a', '--success-bg': '#0f172a', '--success-text': '#ffffff', '--error-bg': '#0f172a', '--error-text': '#ffffff', '--info-bg': '#0f172a', '--info-text': '#ffffff', '--warning-bg': '#0f172a', '--warning-text': '#ffffff' }}
            toastOptions={{
                className: 'bbm-toast',
                style: { fontFamily: 'inherit', fontWeight: 600, borderRadius: 10, padding: '10px 14px' },
            }}
        />
    );
}

// ── Loading (3B) ──────────────────────────────────────────────────────────
// Rows-shaped placeholder that matches a ruled list / ledger table so the
// layout doesn't jump when data lands. Drop it INSIDE the panel.
export function RowSkeleton({ rows = 5, height = 'h-12', className = '' }) {
    return (
        <div className={`divide-y divide-slate-100 ${className}`} aria-busy="true" aria-label="Loading">
            {Array.from({ length: rows }).map((_, i) => (
                <div key={i} className={`flex items-center gap-4 px-4 ${height}`}>
                    <Skeleton className="h-3" style={{ width: `${110 + (i % 4) * 22}px` }} />
                    <Skeleton className="h-2.5 flex-1" style={{ maxWidth: `${220 + (i % 3) * 40}px` }} />
                    <Skeleton className="h-5 w-16 ml-auto" />
                </div>
            ))}
        </div>
    );
}

// Card-shaped placeholders for the entity grids.
export function CardSkeleton({ count = 3, className = '' }) {
    return (
        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`} aria-busy="true" aria-label="Loading">
            {Array.from({ length: count }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <Skeleton className="w-10 h-10" rounded="rounded-full" />
                        <div className="flex-1 space-y-2"><Skeleton className="h-3 w-2/3" /><Skeleton className="h-2.5 w-1/3" /></div>
                    </div>
                    <Skeleton className="h-2.5 w-full" /><Skeleton className="h-2.5 w-5/6" />
                </div>
            ))}
        </div>
    );
}

// ── Errors (4B) ───────────────────────────────────────────────────────────
// Short messages only ("Required", "Pick a time") — Jaylen's note on 4B.
export function FieldError({ children, className = '' }) {
    if (!children) return null;
    return <p className={`text-xs font-semibold text-red-600 mt-1 ${className}`}>{children}</p>;
}

// Summary banner at the top of a form. `items` = ['Phone number — required', …].
export function FormErrorBanner({ title, items = [], className = '' }) {
    if (!items.length) return null;
    const heading = title || (items.length === 1 ? 'Fix 1 thing before saving' : `Fix ${items.length} things before saving`);
    return (
        <div role="alert" className={`flex gap-3 items-start rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-[13px] text-red-800 ${className}`}>
            <span className="font-black leading-none mt-0.5">✕</span>
            <div className="min-w-0">
                <div className="font-bold">{heading}</div>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                    {items.map((it, i) => <li key={i}>{it}</li>)}
                </ul>
            </div>
        </div>
    );
}

// Failed panel / page: same dashed card as EmptyState, red mark, Try again.
export function ErrorState({ title = 'Couldn’t load this', hint = 'The server didn’t answer. Your work is safe — try again in a moment.', onRetry, retryLabel = 'Try again', showReload = true, detail, className = '' }) {
    return (
        <div role="alert" className={`bg-white border border-dashed border-slate-300 rounded-xl px-4 py-8 flex flex-col items-center text-center gap-2 ${className}`}>
            <div className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center font-black text-lg">!</div>
            <p className="text-[15px] font-bold text-slate-800">{title}</p>
            {hint && <p className="text-sm text-slate-500 max-w-[38ch]">{hint}</p>}
            {detail && <pre className="text-[11px] text-slate-400 bg-slate-50 rounded-md px-2 py-1 max-w-full overflow-x-auto">{detail}</pre>}
            <div className="flex items-center gap-2 mt-2">
                {onRetry && <Button variant="secondary" size="sm" onClick={onRetry}>{retryLabel}</Button>}
                {showReload && <Button variant="tertiary" size="sm" onClick={() => window.location.reload()}>Reload page</Button>}
            </div>
        </div>
    );
}

// ── Autosave (5C) ─────────────────────────────────────────────────────────
// Footer status line. status: 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
export function AutosaveStatus({ status = 'idle', savedAt, error, className = '' }) {
    const ago = savedAt ? relTime(savedAt) : null;
    const map = {
        idle: { text: savedAt ? `✓ Saved ${ago}` : '✓ All changes saved', cls: 'text-emerald-600' },
        dirty: { text: 'Unsaved changes…', cls: 'text-amber-600' },
        saving: { text: 'Saving…', cls: 'text-slate-500' },
        saved: { text: `✓ Saved ${ago || 'just now'}`, cls: 'text-emerald-600' },
        error: { text: error ? `Not saved — ${error}` : 'Not saved', cls: 'text-red-600' },
    };
    const m = map[status] || map.idle;
    return <span aria-live="polite" className={`text-xs font-semibold ${m.cls} ${className}`}>{m.text}</span>;
}

function relTime(ts) {
    const s = Math.max(0, Math.round((Date.now() - ts) / 1000));
    if (s < 8) return 'just now';
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m} min ago`;
    return new Date(ts).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export default { AppToaster, RowSkeleton, CardSkeleton, FieldError, FormErrorBanner, ErrorState, AutosaveStatus };
